import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { RuleTrigger } from '../types';

interface RuleTriggerListProps {
  triggers: RuleTrigger[];
  title?: string;
}

const severityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
};

export default function RuleTriggerList({ triggers, title = 'Score Changes' }: RuleTriggerListProps) {
  const user = useAuthStore((s) => s.user);
  const scoringPath = user ? `/${user.role}/scoring` : '/vendor/scoring';

  if (!triggers.length) {
    return (
      <div className="text-sm text-slate-400 py-4">
        No rules triggered yet.
      </div>
    );
  }

  const positive = triggers.filter(t => t.is_positive);
  const negative = triggers.filter(t => !t.is_positive);

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-700 mb-3">{title}</h3>

      {negative.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-red-500 mb-1.5 uppercase tracking-wide">Penalties</p>
          <div className="space-y-1.5">
            {negative.map(t => (
              <TriggerRow key={t.id} trigger={t} />
            ))}
          </div>
        </div>
      )}

      {positive.length > 0 && (
        <div>
          <p className="text-xs font-medium text-emerald-500 mb-1.5 uppercase tracking-wide">Rewards</p>
          <div className="space-y-1.5">
            {positive.map(t => (
              <TriggerRow key={t.id} trigger={t} />
            ))}
          </div>
        </div>
      )}

      {/* Link to full scoring history */}
      <div className="mt-4 pt-3 border-t border-slate-100">
        <Link
          to={scoringPath}
          className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
        >
          View All Scoring History
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

function TriggerRow({ trigger }: { trigger: RuleTrigger }) {
  const impact = Number(trigger.score_impact);
  const sign = impact > 0 ? '+' : '';

  return (
    <div className="flex items-start gap-2 text-sm">
      <span className={`flex-shrink-0 mt-0.5 font-mono text-xs font-semibold w-12 text-right ${
        impact > 0 ? 'text-emerald-600' : 'text-red-600'
      }`}>
        {sign}{impact.toFixed(1)}
      </span>
      <span className="text-slate-600 flex-1">{trigger.explanation}</span>
      <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${
        severityColors[trigger.severity_level] || severityColors.low
      }`}>
        {trigger.severity_level}
      </span>
    </div>
  );
}
